// /app/frontend/src/pages/compliance/maturity/MaturityControlPage.jsx
/**
 * TR2UST v2.0 - Control Assessment Page
 * Dark Mode Support with Maturity Levels at Top
 */
import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate, useSearchParams, Link } from "react-router-dom";
import { useAuth } from "../../../contexts/AuthContext";
import { maturityAPI } from "../../../services/api";
import { Card, CardContent, CardHeader, CardTitle } from "../../../components/ui/card";
import { Button } from "../../../components/ui/button";
import { Badge } from "../../../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../../components/ui/tabs";
import { Textarea } from "../../../components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../../../components/ui/dialog";
import { toast } from "sonner";
import DashboardLayout from "../../../components/layout/DashboardLayout";
import { MATURITY_LEVELS } from "./maturityConfig";
import {
  ArrowLeft,
  ArrowRight,
  Save,
  Upload,
  Trash2,
  Download,
  FileText,
  CheckCircle,
  AlertTriangle,
  MinusCircle,
  Home,
  ChevronRight,
  Lightbulb,
  AlertOctagon,
  Zap,
  Scale,
  X,
} from "lucide-react";

// Tier configurations
const TIER_CONFIG = {
  T1: {
    name: "Foundational",
    bg: "bg-emerald-500/10 dark:bg-emerald-500/20",
    text: "text-emerald-700 dark:text-emerald-400",
  },
  T2: { name: "Standard", bg: "bg-blue-500/10 dark:bg-blue-500/20", text: "text-blue-700 dark:text-blue-400" },
  T3: { name: "Advanced", bg: "bg-violet-500/10 dark:bg-violet-500/20", text: "text-violet-700 dark:text-violet-400" },
};

// Maturity Level Card Component
function MaturityLevelCard({ level, isSelected, onSelect, control, isReadOnly }) {
  // Get short descriptor if available (Change 2)
  const shortDesc = control?.maturity_short_descriptors?.[`level_${level.level}`];

  return (
    <button
      onClick={() => !isReadOnly && onSelect(level.level)}
      disabled={isReadOnly}
      className={`
        flex-1 min-w-[140px] p-4 rounded-xl border-2 transition-all duration-200 text-left
        ${
          isSelected
            ? "border-current shadow-md ring-2 ring-offset-2 dark:ring-offset-slate-900"
            : "bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 hover:border-slate-300 dark:hover:border-slate-600 hover:shadow-sm"
        }
        ${isReadOnly ? "opacity-60 cursor-not-allowed" : ""}
      `}
      style={
        isSelected
          ? {
              backgroundColor: `${level.color}15`,
              borderColor: level.color,
              "--tw-ring-color": level.color,
            }
          : {}
      }
      data-testid={`level-${level.level}-btn`}
    >
      <div className="flex items-center gap-2 mb-2">
        <span
          className="w-8 h-8 rounded-full flex items-center justify-center text-white font-bold text-sm"
          style={{ backgroundColor: level.color }}
        >
          {level.level}
        </span>
        <span
          className={`font-semibold text-sm ${isSelected ? "" : "text-slate-900 dark:text-white"}`}
          style={isSelected ? { color: level.color } : {}}
        >
          {level.name}
        </span>
      </div>
      {shortDesc && (
        <p
          className={`text-xs line-clamp-2 ${isSelected ? "" : "text-slate-500 dark:text-slate-400"}`}
          style={isSelected ? { color: level.color } : {}}
        >
          {shortDesc}
        </p>
      )}
    </button>
  );
}

export default function MaturityControlPage() {
  const { controlId } = useParams();
  const [searchParams] = useSearchParams();
  const entityId = searchParams.get("entity");
  const navigate = useNavigate();
  const { user, isComplianceReadOnly: isReadOnly } = useAuth();
  const fileInputRef = useRef(null);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [control, setControl] = useState(null);
  const [assessment, setAssessment] = useState(null);
  const [selectedLevel, setSelectedLevel] = useState(null);
  const [isNA, setIsNA] = useState(false);
  const [naReason, setNaReason] = useState("");
  const [notes, setNotes] = useState("");
  const [evidenceFiles, setEvidenceFiles] = useState([]);
  const [showNADialog, setShowNADialog] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [allControls, setAllControls] = useState([]);
  const [tierInfo, setTierInfo] = useState(null);
  const [filteredControls, setFilteredControls] = useState([]);

  useEffect(() => {
    const loadData = async () => {
      if (!controlId || !entityId) return;

      // Spec §4: the snapshot prompt must appear AT MOST once per browser
      // session. The `snapshotPromptShown` flag is intentionally NOT reset
      // here — resetting it would cause the toast to re-fire on every
      // dashboard/domain round-trip.

      setLoading(true);
      try {
        const [controlRes, assessmentRes, controlsRes, tierRes] = await Promise.all([
          maturityAPI.getControl(controlId),
          maturityAPI.getControlAssessment(entityId, controlId).catch(() => ({ data: null })),
          maturityAPI.getDomainControls(controlId.split("-")[0]),
          maturityAPI.getTier(entityId),
        ]);

        setControl(controlRes.data);
        const allCtrls = controlsRes.data || [];
        setAllControls(allCtrls);
        setTierInfo(tierRes.data);

        // Filter controls by tier
        const entityTier = tierRes.data?.maturity_tier || 'T1';
        const tiersToInclude = entityTier === 'T1' ? ['T1'] : entityTier === 'T2' ? ['T1', 'T2'] : ['T1', 'T2', 'T3'];
        const filtered = allCtrls.filter((c) => tiersToInclude.includes(c.tier));
        setFilteredControls(filtered);

        // Reset form state (in case previous control had data)
        setAssessment(null);
        setSelectedLevel(null);
        setIsNA(false);
        setNaReason("");
        setNotes("");
        setEvidenceFiles([]);

        // Load existing assessment if available
        if (assessmentRes.data) {
          setAssessment(assessmentRes.data);
          setSelectedLevel(assessmentRes.data.maturity_level);
          setIsNA(assessmentRes.data.is_na || false);
          setNaReason(assessmentRes.data.na_reason || "");
          setNotes(assessmentRes.data.notes || "");
        }

        try {
          const evidenceRes = await maturityAPI.getEvidence(entityId, controlId);
          setEvidenceFiles(evidenceRes.data || []);
        } catch (e) {
          setEvidenceFiles([]);
        }
      } catch (error) {
        toast.error("Failed to load control");
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [controlId, entityId]);

  const handleSave = async () => {
    if (!selectedLevel && !isNA) {
      toast.error("Please select a maturity level or mark as N/A");
      return;
    }

    setSaving(true);
    try {
      await maturityAPI.submitAssessment(entityId, {
        control_id: controlId,
        maturity_level: isNA ? null : selectedLevel,
        is_na: isNA,
        na_reason: isNA ? naReason : null,
        notes: notes,
      });
      // Track assessment for snapshot prompt (Change 4). Also remember the
      // entity so the "fire on leaving module" listener knows which snapshot
      // to save when the user transitions outside /compliance/maturity/*.
      sessionStorage.setItem('maturityAssessedInSession', 'true');
      sessionStorage.setItem('maturityAssessedEntityId', entityId);
      toast.success("Assessment saved!");
    } catch (error) {
      toast.error("Failed to save assessment");
    } finally {
      setSaving(false);
    }
  };

  const handleNA = () => setShowNADialog(true);
  const confirmNA = () => {
    setIsNA(true);
    setSelectedLevel(null);
    setShowNADialog(false);
    toast.info("Control marked as N/A");
  };
  const clearNA = () => {
    setIsNA(false);
    setNaReason("");
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 10 * 1024 * 1024) {
      toast.error("File size must be less than 10MB");
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      await maturityAPI.uploadEvidence(entityId, controlId, formData);
      toast.success("Evidence uploaded!");
      const evidenceRes = await maturityAPI.getEvidence(entityId, controlId);
      setEvidenceFiles(evidenceRes.data || []);
    } catch (error) {
      toast.error("Failed to upload evidence");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleDeleteEvidence = async (fileId) => {
    try {
      await maturityAPI.deleteEvidence(fileId);
      setEvidenceFiles((prev) => prev.filter((f) => f.id !== fileId));
      toast.success("Evidence deleted");
    } catch (error) {
      toast.error("Failed to delete evidence");
    }
  };

  const handleDownloadEvidence = async (fileId) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/maturity/evidence/download/${fileId}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });

      if (!response.ok) {
        throw new Error(`Download failed: ${response.statusText}`);
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error("Failed to download evidence");
    }
  };

  // Use filtered controls for navigation to respect tier filtering
  const navControls = filteredControls.length > 0 ? filteredControls : allControls;
  const currentIndex = navControls.findIndex((c) => c.control_id === controlId);
  const prevControl = currentIndex > 0 ? navControls[currentIndex - 1] : null;
  const nextControl = currentIndex < navControls.length - 1 ? navControls[currentIndex + 1] : null;

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!control) {
    return (
      <div className="p-6">
        <Card className="bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700">
          <CardContent className="py-12 text-center">
            <AlertTriangle className="h-12 w-12 text-amber-500 mx-auto mb-4" />
            <p className="text-lg font-medium text-slate-900 dark:text-white">Control not found</p>
            <Button variant="outline" className="mt-4" onClick={() => navigate("/compliance/maturity")}>
              Back to Dashboard
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const tierConfig = TIER_CONFIG[control.tier] || TIER_CONFIG.T1;

  const pageContent = (
    <div className="p-6 max-w-6xl mx-auto space-y-6" style={{ fontFamily: "Inter, sans-serif" }}>
      {/* Breadcrumb */}
      <nav className="flex items-center gap-2 text-sm text-slate-500 dark:text-slate-400">
        <Link
          to="/dashboard"
          className="hover:text-blue-600 dark:hover:text-blue-400 flex items-center gap-1 transition-colors"
        >
          <Home className="h-4 w-4" strokeWidth={1.5} />
          <span>Dashboard</span>
        </Link>
        <ChevronRight className="h-4 w-4" />
        <Link to="/compliance/maturity" className="hover:text-blue-600 dark:hover:text-blue-400 transition-colors">
          Maturity Assessment
        </Link>
        <ChevronRight className="h-4 w-4" />
        <Link
          to={`/compliance/maturity/domain/${control.domain}?entity=${entityId}`}
          className="hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
        >
          {control.domain}
        </Link>
        <ChevronRight className="h-4 w-4" />
        <span className="text-slate-900 dark:text-white font-medium">{control.control_id}</span>
      </nav>

      {/* Back & Navigation */}
      <div className="flex items-center justify-between">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate(`/compliance/maturity/domain/${control.domain}?entity=${entityId}`)}
          className="hover:bg-slate-100 dark:hover:bg-slate-800"
          data-testid="back-btn"
        >
          <ArrowLeft className="h-4 w-4 mr-2" strokeWidth={1.5} />
          Back to {control.domain}
        </Button>

        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            disabled={!prevControl}
            onClick={() => navigate(`/compliance/maturity/control/${prevControl?.control_id}?entity=${entityId}`)}
          >
            <ArrowLeft className="h-4 w-4 mr-1" strokeWidth={1.5} /> Prev
          </Button>
          <span className="text-sm text-slate-500 dark:text-slate-400 px-2">
            {currentIndex + 1} / {navControls.length}
          </span>
          <Button
            variant="outline"
            size="sm"
            disabled={!nextControl}
            onClick={() => navigate(`/compliance/maturity/control/${nextControl?.control_id}?entity=${entityId}`)}
          >
            Next <ArrowRight className="h-4 w-4 ml-1" strokeWidth={1.5} />
          </Button>
        </div>
      </div>

      {/* Control Header Card */}
      <Card className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 shadow-sm">
        <CardContent className="p-6">
          <div className="flex items-start gap-4">
            <div className="flex items-center gap-2">
              <span
                className="inline-flex items-center justify-center px-3 py-1.5 rounded-lg text-white font-bold text-sm"
                style={{ backgroundColor: "#3B82F6", fontFamily: "JetBrains Mono, monospace" }}
              >
                {control.control_id}
              </span>
              <span
                className={`inline-flex items-center px-2.5 py-1 rounded text-xs font-semibold ${tierConfig.bg} ${tierConfig.text}`}
              >
                {control.tier}
              </span>
            </div>
            <div className="flex-1">
              <h1
                className="text-xl font-bold text-slate-900 dark:text-white mb-1"
                style={{ fontFamily: "Plus Jakarta Sans, sans-serif" }}
              >
                {control.title}
              </h1>
              <p className="text-sm text-slate-500 dark:text-slate-400">Weight: {control.control_weight}%</p>
            </div>
            {isNA && (
              <Badge className="bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 border border-slate-200 dark:border-slate-600 px-3 py-1.5">
                <MinusCircle className="h-4 w-4 mr-1.5" strokeWidth={1.5} /> N/A
              </Badge>
            )}
          </div>

          <div className="mt-4 p-4 bg-slate-50 dark:bg-slate-900/50 rounded-lg">
            <div className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-2">
              What to look for
            </div>
            <p className="text-sm text-slate-700 dark:text-slate-300">{control.description}</p>
          </div>
        </CardContent>
      </Card>

      {/* Maturity Level Selection - TOP CARDS */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h2
            className="text-lg font-semibold text-slate-900 dark:text-white"
            style={{ fontFamily: "Plus Jakarta Sans, sans-serif" }}
          >
            Maturity Level
          </h2>
          {!isReadOnly && (
            !isNA ? (
              <Button
                variant="ghost"
                size="sm"
                onClick={handleNA}
                className="text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200"
              >
                <MinusCircle className="h-4 w-4 mr-1.5" strokeWidth={1.5} /> Mark as N/A
              </Button>
            ) : (
              <Button
                variant="ghost"
                size="sm"
                onClick={clearNA}
                className="text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200"
              >
                <X className="h-4 w-4 mr-1.5" strokeWidth={1.5} /> Clear N/A
              </Button>
            )
          )}
        </div>

        {isNA ? (
          <Card className="bg-slate-50 dark:bg-slate-900/50 border border-slate-200 dark:border-slate-700">
            <CardContent className="p-6 text-center">
              <MinusCircle className="h-10 w-10 text-slate-400 mx-auto mb-3" strokeWidth={1.5} />
              <p className="text-slate-600 dark:text-slate-300 font-medium">This control is marked as Not Applicable</p>
              {naReason && <p className="text-sm text-slate-500 dark:text-slate-400 mt-2">Reason: {naReason}</p>}
            </CardContent>
          </Card>
        ) : (
          <div className="flex gap-3 flex-wrap">
            {MATURITY_LEVELS.map((level) => (
              <MaturityLevelCard
                key={level.level}
                level={level}
                isSelected={selectedLevel === level.level}
                onSelect={setSelectedLevel}
                control={control}
                isReadOnly={isReadOnly}
              />
            ))}
          </div>
        )}
      </div>

      {/* Assessment Notes */}
      <Card className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 shadow-sm">
        <CardHeader className="pb-3">
          <CardTitle className="text-base font-semibold flex items-center gap-2 text-slate-900 dark:text-white">
            <FileText className="h-5 w-5 text-slate-400" strokeWidth={1.5} />
            Assessment Notes
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Textarea
            placeholder="Document your assessment rationale, observations, and any relevant context..."
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            disabled={isReadOnly}
            className="min-h-[120px] resize-none bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-700"
            data-testid="notes-textarea"
          />
        </CardContent>
      </Card>

      {/* Evidence Upload */}
      <Card className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 shadow-sm">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base font-semibold flex items-center gap-2 text-slate-900 dark:text-white">
              <Upload className="h-5 w-5 text-slate-400" strokeWidth={1.5} />
              Evidence Files
            </CardTitle>
            {!isReadOnly && (
              <Button variant="outline" size="sm" onClick={() => fileInputRef.current?.click()} disabled={uploading}>
                {uploading ? "Uploading..." : "Upload File"}
              </Button>
            )}
            <input
              ref={fileInputRef}
              type="file"
              className="hidden"
              onChange={handleFileUpload}
              accept=".pdf,.docx,.xlsx,.jpg,.jpeg,.png,.txt,.csv"
            />
          </div>
        </CardHeader>
        <CardContent>
          {evidenceFiles.length === 0 ? (
            <div className="text-center py-8 text-slate-500 dark:text-slate-400">
              <Upload className="h-10 w-10 mx-auto mb-3 text-slate-300 dark:text-slate-600" strokeWidth={1.5} />
              <p className="text-sm">No evidence files uploaded yet</p>
              <p className="text-xs text-slate-400 dark:text-slate-500 mt-1">
                Supported: PDF, DOCX, XLSX, JPG, PNG, TXT, CSV (max 10MB)
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              {evidenceFiles.map((file) => (
                <div
                  key={file.id}
                  className="flex items-center justify-between p-3 bg-slate-50 dark:bg-slate-900/50 rounded-lg"
                >
                  <div className="flex items-center gap-3">
                    <FileText className="h-5 w-5 text-slate-400" strokeWidth={1.5} />
                    <div>
                      <p className="text-sm font-medium text-slate-700 dark:text-slate-300">{file.file_name}</p>
                      <p className="text-xs text-slate-500 dark:text-slate-400">
                        {new Date(file.uploaded_at).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button variant="ghost" size="sm" onClick={() => handleDownloadEvidence(file.id)}>
                      <Download className="h-4 w-4" strokeWidth={1.5} />
                    </Button>
                    {!isReadOnly && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDeleteEvidence(file.id)}
                        className="text-red-500 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20"
                      >
                        <Trash2 className="h-4 w-4" strokeWidth={1.5} />
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Guidance Tabs */}
      <Card className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 shadow-sm">
        <CardHeader className="pb-3">
          <CardTitle className="text-base font-semibold text-slate-900 dark:text-white">
            Implementation Guidance
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Tabs defaultValue="notes" className="w-full">
            <TabsList className="grid w-full grid-cols-3 mb-4 bg-slate-100 dark:bg-slate-900">
              <TabsTrigger
                value="notes"
                className="flex items-center gap-2 data-[state=active]:bg-white dark:data-[state=active]:bg-slate-800"
              >
                <Lightbulb className="h-4 w-4" strokeWidth={1.5} /> How to Implement
              </TabsTrigger>
              <TabsTrigger
                value="pitfalls"
                className="flex items-center gap-2 data-[state=active]:bg-white dark:data-[state=active]:bg-slate-800"
              >
                <AlertOctagon className="h-4 w-4" strokeWidth={1.5} /> Common Pitfalls
              </TabsTrigger>
              <TabsTrigger
                value="quickwins"
                className="flex items-center gap-2 data-[state=active]:bg-white dark:data-[state=active]:bg-slate-800"
              >
                <Zap className="h-4 w-4" strokeWidth={1.5} /> Quick Wins
              </TabsTrigger>
            </TabsList>

            <TabsContent value="notes" className="mt-0">
              <div className="p-4 bg-blue-500/10 dark:bg-blue-500/20 rounded-lg">
                <p className="text-sm text-blue-800 dark:text-blue-300">
                  {control.implementation_notes || "No implementation notes available."}
                </p>
              </div>
            </TabsContent>

            <TabsContent value="pitfalls" className="mt-0">
              <div className="p-4 bg-amber-500/10 dark:bg-amber-500/20 rounded-lg">
                {control.common_pitfalls?.length > 0 ? (
                  <ul className="space-y-2">
                    {control.common_pitfalls.map((pitfall, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-sm text-amber-800 dark:text-amber-300">
                        <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" strokeWidth={1.5} />
                        {pitfall}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-sm text-amber-800 dark:text-amber-300">No common pitfalls documented.</p>
                )}
              </div>
            </TabsContent>

            <TabsContent value="quickwins" className="mt-0">
              <div className="p-4 bg-emerald-500/10 dark:bg-emerald-500/20 rounded-lg">
                {control.quick_wins?.length > 0 ? (
                  <ul className="space-y-2">
                    {control.quick_wins.map((win, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-sm text-emerald-800 dark:text-emerald-300">
                        <CheckCircle className="h-4 w-4 mt-0.5 flex-shrink-0" strokeWidth={1.5} />
                        {win}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-sm text-emerald-800 dark:text-emerald-300">No quick wins documented.</p>
                )}
              </div>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      {/* Save Button */}
      <div className="flex items-center justify-between pt-4 border-t border-slate-200 dark:border-slate-700">
        <div className="text-sm text-slate-500 dark:text-slate-400">
          {assessment
            ? `Last updated: ${new Date(assessment.updated_at || assessment.created_at).toLocaleString()}`
            : "Not yet assessed"}
        </div>
        <div className="flex items-center gap-3">
          <Button
            variant="outline"
            onClick={() => navigate(`/compliance/maturity/domain/${control.domain}?entity=${entityId}`)}
          >
            Cancel
          </Button>
          {!isReadOnly && (
            <Button
              onClick={handleSave}
              disabled={saving || (!selectedLevel && !isNA)}
              className="bg-blue-600 hover:bg-blue-700"
              data-testid="save-btn"
            >
              {saving ? "Saving..." : "Save Assessment"}
              <Save className="h-4 w-4 ml-2" strokeWidth={1.5} />
            </Button>
          )}
        </div>
      </div>

      {/* N/A Dialog */}
      <Dialog open={showNADialog} onOpenChange={setShowNADialog}>
        <DialogContent className="bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700">
          <DialogHeader>
            <DialogTitle className="text-slate-900 dark:text-white">Mark as Not Applicable?</DialogTitle>
            <DialogDescription className="text-slate-500 dark:text-slate-400">
              This control will be excluded from scoring calculations. You can provide an optional reason.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Textarea
              placeholder="Reason for N/A (optional)..."
              value={naReason}
              onChange={(e) => setNaReason(e.target.value)}
              className="min-h-[80px] bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-700"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNADialog(false)}>
              Cancel
            </Button>
            {!isReadOnly && (
              <Button onClick={confirmNA}>Confirm N/A</Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );

  return <DashboardLayout>{pageContent}</DashboardLayout>;
}
